import { useEffect, useRef } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { buildSearchParams } from '@/client/lib/searchParams';

/**
 * Hook that persists URL search params to sessionStorage and restores them
 * when returning to a route without params.
 *
 * This solves the problem where sidebar navigation clears search params.
 *
 * @param routePath - The route path (e.g., '/encoders', '/clients')
 * @param defaultValues - Default values for the search params (used to avoid storing defaults)
 */
export function usePersistedSearch<T extends Record<string, unknown>>(
  routePath: string,
  defaultValues: T
): void {
  const navigate = useNavigate();
  const router = useRouter();
  const search = router.state.location.search as T;
  const hasRestored = useRef(false);
  const storageKey = `search:${routePath}`;

  // On mount: restore from sessionStorage if URL has no params
  useEffect(() => {
    if (hasRestored.current) return;
    hasRestored.current = true;

    // Check if URL has any non-default params
    const hasUrlParams = Object.entries(search).some(([key, value]) => {
      const defaultValue = defaultValues[key as keyof T];
      return value !== undefined && value !== defaultValue;
    });

    if (hasUrlParams) {
      // URL has params, save them
      sessionStorage.setItem(storageKey, JSON.stringify(search));
      return;
    }

    // Try to restore from sessionStorage
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as T;
        // Filter out default values and undefined
        const nonDefaultParams = Object.entries(parsed).reduce((acc, [key, value]) => {
          const defaultValue = defaultValues[key as keyof T];
          if (value !== undefined && value !== defaultValue && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {} as Record<string, unknown>);

        if (Object.keys(nonDefaultParams).length > 0) {
          navigate({
            to: routePath,
            search: buildSearchParams(nonDefaultParams),
            replace: true, // Don't add to history
          });
        }
      } catch {
        sessionStorage.removeItem(storageKey);
      }
    }
  }, []);

  // On search change: save to sessionStorage
  useEffect(() => {
    if (!hasRestored.current) return;

    const hasNonDefaultParams = Object.entries(search).some(([key, value]) => {
      const defaultValue = defaultValues[key as keyof T];
      return value !== undefined && value !== defaultValue;
    });

    if (hasNonDefaultParams) {
      sessionStorage.setItem(storageKey, JSON.stringify(search));
    } else {
      // Clear storage if all params are default
      sessionStorage.removeItem(storageKey);
    }
  }, [search, storageKey, defaultValues]);
}
